import { X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './utils';

export interface ThreadTab {
	id: string;
	label: string;
	/** Unread messages in that thread — a filled badge. */
	unread?: number;
	/** Messages in it at all — shown muted when nothing is unread, so an empty
	 *  thread reads as empty and a busy one as busy. */
	count?: number;
}

interface ThreadPanelProps {
	open: boolean;
	onClose: () => void;
	title: ReactNode;
	subtitle?: ReactNode;
	/** One per thread — "Studio notes", then a supplier each. Omit for a single thread. */
	tabs?: ThreadTab[];
	activeTab?: string;
	onTabChange?: (id: string) => void;
	/** The active thread — normally a `ThreadView`, which fills the rest. */
	children: ReactNode;
}

/**
 * The side panel a product's threads open in: fixed to the right edge over
 * the page, full width on a phone. Closes on Escape and on the backdrop.
 */
export function ThreadPanel({
	open,
	onClose,
	title,
	subtitle,
	tabs,
	activeTab,
	onTabChange,
	children,
}: ThreadPanelProps) {
	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [open, onClose]);

	if (!open) return null;

	return createPortal(
		<div className="fixed inset-0 z-50 flex justify-end">
			<div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden />
			<aside
				role="dialog"
				aria-modal="true"
				className="relative flex h-full w-full flex-col border-l border-line-1 bg-canvas shadow-xl sm:w-[460px]"
			>
				<header className="flex items-start gap-3 border-b border-line-1 px-4 pt-4 pb-3">
					<div className="min-w-0 flex-1">
						<div className="truncate text-lg font-medium text-text-1">{title}</div>
						{subtitle && (
							<div className="mt-0.5 truncate text-sm text-text-3">{subtitle}</div>
						)}
					</div>
					<button
						type="button"
						aria-label="Close"
						onClick={onClose}
						className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
					>
						<X size={16} />
					</button>
				</header>

				{tabs && tabs.length > 1 && (
					<div className="scroll-thin flex gap-1 overflow-x-auto border-b border-line-1 px-3 py-2">
						{tabs.map((t) => (
							<button
								key={t.id}
								type="button"
								onClick={() => onTabChange?.(t.id)}
								className={cn(
									'flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1 text-sm transition-colors',
									t.id === activeTab
										? 'bg-surface-fill font-medium text-text-1'
										: 'text-text-3 hover:text-text-1'
								)}
							>
								{t.label}
								{t.unread ? (
									<span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-2xs font-medium text-primary-foreground">
										{t.unread > 9 ? '9+' : t.unread}
									</span>
								) : (
									!!t.count && (
										<span className="text-xs text-text-4">{t.count}</span>
									)
								)}
							</button>
						))}
					</div>
				)}

				<div className="flex min-h-0 flex-1 flex-col">{children}</div>
			</aside>
		</div>,
		document.body
	);
}
